/**
 * StockClient
 * Anonymous SignalR client for real-time stock updates.
 * Connects to the StockHub WebSocket service without authentication.
 * Clients join event groups to receive stock change broadcasts.
 */
import { BaseSignalRClient } from '../shared/BaseSignalRClient';
export class StockClient extends BaseSignalRClient {
    constructor(hubUrl, options = {}) {
        // Anonymous connection — no token required (shop is public)
        super(hubUrl, async () => null, options);
        this.subscribedEvents = new Set();
    }
    /**
     * Join an event's stock update stream.
     * After joining, you'll receive 'stockUpdated' events for this event.
     */
    async joinEvent(eventId) {
        await this.invoke('JoinEvent', eventId);
        this.subscribedEvents.add(eventId);
    }
    /**
     * Leave an event's stock update stream.
     */
    async leaveEvent(eventId) {
        await this.invoke('LeaveEvent', eventId);
        this.subscribedEvents.delete(eventId);
    }
    /**
     * Get the set of currently subscribed event IDs.
     */
    getSubscribedEvents() {
        return new Set(this.subscribedEvents);
    }
    /**
     * Register server-to-client event handlers.
     */
    setupServerMethods() {
        if (!this.connection)
            return;
        // Stock level changed (after purchase, restock, cancellation, etc.)
        this.connection.on('StockUpdated', (update) => {
            this.emit('stockUpdated', update);
        });
        // Confirmation: successfully joined an event group
        this.connection.on('JoinedEvent', (eventId) => {
            this.emit('joinedEvent', eventId);
        });
        // Confirmation: successfully left an event group
        this.connection.on('LeftEvent', (eventId) => {
            this.emit('leftEvent', eventId);
        });
    }
    /**
     * Override connect to re-join previously subscribed events after reconnection.
     */
    async connect() {
        const previousEvents = new Set(this.subscribedEvents);
        await super.connect();
        // Re-subscribe to previously joined events
        for (const eventId of previousEvents) {
            try {
                await this.joinEvent(eventId);
            }
            catch (error) {
                console.warn(`[StockClient] Failed to re-join event ${eventId} after reconnect:`, error);
            }
        }
    }
    /**
     * Override disconnect to clear subscription state.
     */
    async disconnect() {
        this.subscribedEvents.clear();
        await super.disconnect();
    }
}
//# sourceMappingURL=StockClient.js.map